import { RegistrationApi } from 'api';
import { GenericWarningMarkIcon } from 'assets/icons';
import { loginUser, showLoginModal } from 'features/login/store/login.action';
import { RegistrationFormData } from 'features/registration/interface/registration';
import isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import ErrorCard from 'shared/components/dialog/errorCard';
import FormSuccessDialog from 'shared/components/dialog/FormSuccessDialog';
import FormModal from 'shared/components/form/formModal';
import { HelmetMetaTags } from 'shared/components/HelmetMetaTags';
import { PudzianAISuccess } from 'shared/components/PudzianAISuccess/PudzianAISuccess';
import { APP_URL, SectionNavigation } from 'shared/constants/common';
import { DATALAYER_REGISTER_FAIL, RegisterEvents, RegisterPromotionPickerEvents, setDataLayer } from 'shared/constants/datalayer';
import { useShowPudzianVsAiAfterRegistration } from 'shared/featureFlags';
import { useRecaptcha } from 'shared/hooks/useRecaptcha';
import useSectionNavigation from 'shared/hooks/useSectionNavigation';
import { CouponState, State } from 'shared/interface';
import { findCookieValue, isLoggedin } from 'shared/util/utility';
import RegistrationForm from '../component/RegistrationForm';
import { hideRegisterModal } from '../store/register.action';
import { getRegistrationPayloadData, registrationFormInitialValues } from '../utility/utility';

type RegistrationDialogType = '' | 'success' | 'error';

const Registration: React.FC = () => {
	const { generateViewHash } = useSectionNavigation();
	const { goToRootView, getViewParams, changeNavigationView } = useSectionNavigation();
	const renderPudzianSuccess = useShowPudzianVsAiAfterRegistration().value;
	const { stepId } = getViewParams();
	const { t } = useTranslation();
	const location = useLocation();
	const history = useHistory();
	const dispatch = useDispatch();
	const numberOfSteps = 9;
	const [dialogType, setDialogType] = useState<RegistrationDialogType>('');
	const [dialogContent, setDialogContent] = useState('');
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const step = parseInt(stepId!);
	const [loading, setLoading] = useState(false);
	const [emailVerification, setEmailVerification] = useState(false);
	const [formData, setFormData] = useState<RegistrationFormData>(registrationFormInitialValues);
	const [scratchedCode, setScratchedCode] = useState(false);

	const isRegistrationOpen = useSelector((state: State) => state.registerModal.step1RegisterModalOpen);
	const showMobileRegistration = location.pathname === APP_URL.registrationForm;
	const coupon = useSelector<State, CouponState>((state) => state.coupon);
	const { execute: executeRecaptcha } = useRecaptcha();

	const handleNextStep = (formData: RegistrationFormData) => {
		setFormData(formData);

		if (step === numberOfSteps) {
			onRegistration({ ...formData });
			return;
		} else if (step === 6 && emailVerification) {
			changeNavigationView(SectionNavigation.REGISTRATION, { stepId: 8 });
		} else {
			changeNavigationView(SectionNavigation.REGISTRATION, { stepId: step + 1 });
		}
	};

	const handlePrevStep = (formData: RegistrationFormData) => {
		setFormData(formData);
		if (step === 1) {
			handleCloseRegistrationProcess();
			return;
		} else if (step === 8 && emailVerification) {
			changeNavigationView(SectionNavigation.REGISTRATION, { stepId: 6 });
		} else {
			changeNavigationView(SectionNavigation.REGISTRATION, { stepId: step - 1 });
		}
	};

	const handleCloseRegistrationProcess = () => {
		setDialogType('');
		setDialogContent('');
		setLoading(false);
		setFormData(registrationFormInitialValues);

		if (location.pathname === APP_URL.registrationForm) {
			history.push(APP_URL.login, window.location.pathname);
		} else {
			goToRootView();
			dispatch(hideRegisterModal());
			dispatch(showLoginModal());
		}
	};

	const handleCloseErrorPopup = () => {
		setDialogType('');
		setDialogContent('');
		setLoading(false);
	};

	enum EErrorCode {
		SMS_CODE_INVALID_TEXT = 467,
		ILLEGAL_CHARACTERS = 4238,
		LAST_NAME_TOO_SHORT = 4232,
	}

	const sendRegisterFailedDataLayerEvent = (fail_reason: string) => {
		RegisterEvents.FAILED({
			fail_reason,
			reg_type: 'manual',
		});
	};

	const getDialogContent = (errorCode: EErrorCode) => {
		switch (errorCode) {
			case EErrorCode.SMS_CODE_INVALID_TEXT:
				return t('smsCodeInvalidText');
			case EErrorCode.ILLEGAL_CHARACTERS:
				return t('registrationPasswordIllegalCharacters');
			case EErrorCode.LAST_NAME_TOO_SHORT:
				return t('registrationLastNameTooShort');
			default:
				return t('regErrorMessage');
		}
	};
	const onRegistration = async (data: RegistrationFormData) => {
		try {
			setLoading(true);
			const registerObj: any = getRegistrationPayloadData(data);
			if (isEmpty(registerObj.registrationOffers)) {
				delete registerObj.registrationOffers;
			}
			const response = await RegistrationApi.register(registerObj);
			const errorCode = response?.data?.data[0]?.code;
			if (!response?.data?.isError) {
				RegisterPromotionPickerEvents.CHOSEN_PROMOTION({
					registration_promotion_id: data.regPromotion.id,
					registration_promotion_name: data.regPromotion.name,
				});
				const recaptchaToken = await executeRecaptcha('LOGIN');
				await (dispatch(
					loginUser(
						{
							login: data.email,
							password: data.password,
							keepSignedIn: true,
							recaptchaToken,
						},
						coupon,
					),
				) as unknown as Promise<{ isError: boolean }>);

				setDialogType('success');
				history.replace(APP_URL.home);
				RegisterEvents.SUCCESS({
					reg_type: 'manual',
				});
			} else if (response?.data?.data?.length > 0 && errorCode) {
				setDialogContent(getDialogContent(errorCode));
				setDialogType('error');
				setDataLayer(DATALAYER_REGISTER_FAIL);
				sendRegisterFailedDataLayerEvent(`${errorCode} - ${getDialogContent(errorCode)}`);
			} else {
				setDialogType('error');
				setDataLayer(DATALAYER_REGISTER_FAIL);
				setDialogContent(response?.data?.code === 423 ? t('smsCodeInvalidText') : t('regErrorMessage'));
			}
		} catch (error) {
			setDialogType('error');
			setDialogContent(t('regErrorMessage'));
			setDataLayer(DATALAYER_REGISTER_FAIL);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (!isRegistrationOpen) {
			history.replace({
				...location,
				pathname: APP_URL.registrationForm,
				hash: generateViewHash(SectionNavigation.REGISTRATION, { stepId: 1 }),
			});
		}
	}, []);

	useEffect(() => {
		const codeFromCms = queryString.parse(location.search).promotionCode; //kod stworzony jako link w cms
		const codeFromLandingPage = queryString.parse(location.search).promo; // kod z landing page'a
		let regCode = codeFromCms ? codeFromCms : codeFromLandingPage;

		const codeFromCookie = findCookieValue('promo');
		if (codeFromCookie) regCode = codeFromCookie;

		setFormData({ ...formData, regCode });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.search]);

	useEffect(() => {
		if (isLoggedin()) {
			history.replace(APP_URL.home);
		}
	}, [isLoggedin]);

	const renderRegistrationSteps = (
		<RegistrationForm
			currentStep={step}
			numberOfSteps={numberOfSteps}
			handlePrevStep={handlePrevStep}
			emailVerification={emailVerification}
			setEmailVerification={setEmailVerification}
			handleNextStep={handleNextStep}
			initialValues={formData}
			onSubmit={onRegistration}
			t={t}
			isDesktop={!showMobileRegistration}
			handleCloseRegistrationProcess={handleCloseRegistrationProcess}
			loading={loading}
			scratchedCode={scratchedCode}
			setScratchedCode={setScratchedCode}
		/>
	);

	const successScreen = renderPudzianSuccess ? (
		<PudzianAISuccess emailVerification={emailVerification} onClose={handleCloseRegistrationProcess} />
	) : (
		<FormSuccessDialog
			text={emailVerification ? t('registrationFinishByEmailToastText') : t('registrationFinishToastTextActive')}
			closeDialog={handleCloseRegistrationProcess}
			showSuccessConfetti
		/>
	);

	return (
		<>
			<HelmetMetaTags
				title="Rejestracja | BETFAN"
				description="BETFAN to legalny polski bukmacher. Powstaliśmy, aby każdego dnia dostarczać niepowtarzalną i unikatową porcję rozrywki."
			/>

			{showMobileRegistration ? (
				<>
					{renderRegistrationSteps}
					{dialogType === 'success' && successScreen}
				</>
			) : (
				<FormModal open={isRegistrationOpen} close={handleCloseRegistrationProcess}>
					<>{dialogType === 'success' ? successScreen : renderRegistrationSteps}</>
				</FormModal>
			)}

			{dialogType === 'error' && (
				<ErrorCard iconTitle={dialogContent} onClose={handleCloseErrorPopup} icon={GenericWarningMarkIcon} isGenericTextColor />
			)}
		</>
	);
};

export default Registration;
